import React from 'react';
import PropTypes from 'prop-types';
import { get, debounce } from 'lodash';
import { Field, reduxForm } from 'redux-form';
import RadioField from '../../app-main/components/reduxForms/fields/RadioField/RadioField';
import NumberField from '../../app-main/components/reduxForms/fields/NumberField/NumberField';
import { FANDB_FORM } from './constants';
import validateFandBForm from './validate/validateFandBForm';
import { FormattedNumber } from 'react-intl';
import onSubmitPriceForm from './submit/onSubmitPriceForm';
import onSubmitIsFnb from './submit/onSubmitIsFnb';
import onChangeFnbPriceForm from './change/onChangeFnbPriceForm';
import Button from '../../elements-buttons/Button';
import Currency from '../../elements-prices/Currency';
import { FormattedMessage, injectIntl, intlShape, defineMessages } from 'react-intl';

const FndBForm = ({
  initialValues,
  handleSubmit,
  isFandB,
  fandB,
  listingPrice,
  isPricePatching,
  dispatch,
  intl,
  ...props
}) => {
  const { value: totalValue, currency } = get(initialValues, 'prices', {});
  const fnbValue = get(listingPrice, 'value', '') || totalValue;
  const translationMessages = defineMessages({
    yes: {
      id: 'hosting.f_and_b_form.yes',
      defaultMessage: 'Yes'
    },
    no: {
      id: 'hosting.f_and_b_form.no',
      defaultMessage: 'No'
    },
    optionErrorMessage: {
      id: 'hosting.f_and_b_form.option_error_message',
      defaultMessage: 'Please select an option'
    },
    ratePlaceholder: {
      id: 'hosting.f_and_b_form.rate_placeholder',
      defaultMessage: 'Rate'
    },
    rateErrorMessage: {
      id: 'hosting.f_and_b_form.rate_error_message',
      defaultMessage: 'Please enter a valid price'
    },
    foodDrinks: {
      id: 'hosting.f_and_b_form.food_and_drinks',
      defaultMessage: 'Food and drinks'
    },
    drinks: {
      id: 'hosting.f_and_b_form.just_drinks',
      defaultMessage: 'Just drinks'
    },
    foodBar: {
      id: 'hosting.f_and_b_form.food_bar_tab',
      defaultMessage: 'food and bar tab'
    },
    bar: {
      id: 'hosting.f_and_b_form.bar_tab',
      defaultMessage: 'bar tab'
    }
  });

  return (
    <form onSubmit={handleSubmit(onSubmitPriceForm)}>
      <Field name="prices._id" component="input" type="hidden" />
      <div>
        <Field
          onChange={(e, newValue) => onSubmitIsFnb(newValue, dispatch, props)}
          component={RadioField}
          name="fandb.fandb_required"
          className="margin-lg-bottom"
          errorMessage={intl.formatMessage(translationMessages.optionErrorMessage)}
          options={[
            {
              id: 'fandb-yes',
              value: true,
              label: intl.formatMessage(translationMessages.yes)
            },
            {
              id: 'fandb-no',
              value: false,
              label: intl.formatMessage(translationMessages.no)
            }
          ]}
        />
        <div className="clearfix" />
        <hr />
        {
          isFandB &&
            <div>
              <div className="clearfix" />
              <div className="fandb-panel panel-body border margin-lg-top margin-lg-bottom">
                <h4><FormattedMessage id="hosting.f_and_b_form.food_and_bar_pricing" defaultMessage="Food and bar pricing"/></h4>
                <p className="heavy"><FormattedMessage id="hosting.f_and_b_form.food_and_bar_minimum" defaultMessage="Hourly food and bar minimum"/></p>
                <Field
                  onChange={debounce(handleSubmit(onChangeFnbPriceForm), 500)}
                  className="FandBRateInput"
                  preInputElements={
                    <Currency
                      className="input-group-addon"
                      id="base-rate"
                      currency={currency}
                    />
                  }
                  placeholder={intl.formatMessage(translationMessages.ratePlaceholder)}
                  component={NumberField}
                  name="prices.base_value"
                  errorMessage={intl.formatMessage(translationMessages.rateErrorMessage)}
                />
                <p className="margin-xl-top text-small text-gray-dark">
                  <strong><FormattedMessage id="hosting.f_and_b_form.example" defaultMessage="Example:"/></strong><FormattedMessage id="hosting.f_and_b_form.example_text" defaultMessage=" If you set a food and bar tab of $100/hr and the guest books 3 hours, then the guest's first $300 in food and drinks will be included in the price. Do not include tax and tip in this rate."/>
                </p>

                <div className="clearfix" />
                <p className="margin-lg-top heavy">
                  <FormattedMessage id="hosting.f_and_b_form.minimum_applies_to" defaultMessage="Minimum applies to"/>
                </p>
                <Field
                  component={RadioField}
                  name="fandb.fandb_applies_to"
                  className="margin-lg-bottom"
                  errorMessage={intl.formatMessage(translationMessages.optionErrorMessage)}
                  options={[
                    {
                      id: 'food-bev',
                      value: 'FOOD_BEV',
                      label: intl.formatMessage(translationMessages.foodDrinks)
                    },
                    {
                      id: 'bev',
                      value: 'BEV',
                      label: intl.formatMessage(translationMessages.drinks)
                    }
                  ]}
                />

                <div className="clearfix" />
                <p className="heavy margin-lg-top"><FormattedMessage id="hosting.f_and_b_form.tax" defaultMessage="Tax"/></p>
                <Field
                  onChange={debounce(handleSubmit(onChangeFnbPriceForm), 300)}
                  className="FandBTaxInput"
                  postInputElements={<span className="input-group-addon" id="fandb-tax-rate">%</span>}
                  component={NumberField}
                  name="prices.tax_percent"
                  errorMessage={intl.formatMessage(translationMessages.rateErrorMessage)}
                />
                <p className="text-small text-gray-dark margin-md-top"><FormattedMessage id="hosting.f_and_b_form.include_taxes" defaultMessage="Include all state and local taxes."/></p>

                <div className="clearfix" />
                <p className="heavy margin-lg-top"><FormattedMessage id="hosting.f_and_b_form.tip" defaultMessage="Tip"/></p>
                <Field
                  onChange={debounce(handleSubmit(onChangeFnbPriceForm), 300)}
                  className="FandBTipInput"
                  postInputElements={<span className="input-group-addon" id="fandb-tip-rate">%</span>}
                  component={NumberField}
                  name="prices.display_gratuity_percent"
                  errorMessage={intl.formatMessage(translationMessages.rateErrorMessage)}
                />
                <div className="clearfix" />
                <div className="fandb-panel panel panel-default margin-xl-top">
                  <div className="panel-body">
                    <h6><FormattedMessage id="hosting.f_and_b_form.total_hourly_price" defaultMessage="Total hourly price"/></h6>
                    <div className="h3 no-bottom-margin">
                      <FormattedNumber
                        value={fnbValue}
                        currency={currency}
                        style="currency"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />/hr
                    </div>
                    <div className="text-small heavy">
                      {'BEV' === fandB.fandb_applies_to ? intl.formatMessage(translationMessages.bar) : intl.formatMessage(translationMessages.foodBar)}
                    </div>
                    <p className="text-small text-gray-dark"><FormattedMessage id="hosting.f_and_b_form.tax_tip_included" defaultMessage="Tax and tip included"/></p>
                  </div>
                </div>
                <div className="clearfix" />
                <div className="row">
                  <div className="col-sm-4 col-sm-offset-8 text-right">
                    <Button category="primary" isLoading={isPricePatching} />
                  </div>
                </div>
              </div>
              <div className="clearfix" />
              <hr />
            </div>
        }
      </div>
    </form>
  );
};

FndBForm.propTypes = {
  isFandB: PropTypes.bool.isRequired,
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  fandB: PropTypes.object,
  listingPrice: PropTypes.object,
  isPricePatching: PropTypes.bool.isRequired,
  dispatch: PropTypes.func,
  intl: intlShape
};

const config = {
  form: FANDB_FORM,
  enableReinitialize: true,
  validate: validateFandBForm
};

export default reduxForm(config)(injectIntl(FndBForm));
